import { useState } from 'react';
import { testsRun, type ApiError, type TestRunSummary } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, fig, pre, sx, useGuarded, usePage } from './kit';
import { AboutThis } from '../../ui/AboutThis';

const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const secs = (ms: number) => (ms / 1000).toFixed(1) + 's';

/** Tests (design seIsTests, T9445; ts* L21775 and L22290-22307): the real suites, run on demand. */
export function TestsSection() {
  const { client } = useSession();
  const { changed } = usePage();
  const { canWrite, roReason } = useGuarded();
  const [res, setRes] = useState<TestRunSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const run = async () => {
    if (!canWrite || busy) return;
    setBusy(true);
    setErr(null);
    try {
      setRes(await testsRun(client, { body: {} }));
      changed();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const suites = res?.suites ?? [];
  const runners = [...new Set(suites.map((s) => s.runner.replace(' test', '')))].join(' + ');
  const wall = suites.reduce((n, s) => n + s.durationMs, 0);
  const tiles: [string, string, string, string][] = res
    ? [
        ['Suites', String(suites.length), runners || 'none available', '#22272E'],
        ['Passed', String(res.passed), 'of ' + res.total + ' cases', '#12503C'],
        ['Failed', String(res.failed), res.failed ? 'blocks the release' : 'nothing blocking', res.failed ? '#B3261E' : '#12503C'],
        ['Wall time', secs(wall), 'nothing cached', '#22272E'],
      ]
    : [];
  const unavailable = suites.filter((s) => !s.available);

  return (
    <div data-testid="settings-tests">
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;margin-bottom:13px;flex-wrap:wrap;row-gap:8px;')}>
        <span style={sx('font-size:16px;font-weight:700;')}>Test suite</span>
        <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#54606C;')}>POST /api/tests/run</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          role="button"
          aria-disabled={!canWrite || busy || undefined}
          title={roReason ?? undefined}
          onClick={(e) => { pre(e); void run(); }}
          style={sx('display:inline-flex;align-items:center;height:36px;padding:0 16px;border-radius:9px;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;')}
        >
          {'▶  '}
          {I(busy ? 'Running…' : res ? 'Re-run all tests' : 'Run all tests')}
        </a>
      </div>
      <ErrLine error={err} style="margin:-4px 0 10px;" />
      {!res && (
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;')}>
          {/* V9: the explainer opens on the "i" (design k10181). */}
          <AboutThis inline textStyle="font-size:16px;color:#54606C;line-height:1.6;">
            Run the suites to see live results. Every run executes the real jest and flutter tests, so nothing here is cached.
          </AboutThis>
        </div>
      )}
      {res && (
        <>
          <div style={sx('grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px 18px;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);padding:10px 0;margin-bottom:12px;')}>
            {tiles.map((t, i, a) => (
              <div key={t[0]} style={sx('padding:12px 15px;box-sizing:border-box;' + (i < a.length - 1 ? 'border-right:1px solid rgba(34,39,46,.09);' : ''))}>
                <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;margin-bottom:6px;')}>{I(t[0])}</div>
                <div style={sx(fig(t[3], 26))}>{I(t[1])}</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{I(t[2])}</div>
              </div>
            ))}
          </div>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
            <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) 90px 90px 90px;gap:12px;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);')}>
              <span style={sx(CAPT)}>Suite</span>
              <span style={sx(CAPT + 'text-align:right;')}>Passed</span>
              <span style={sx(CAPT + 'text-align:right;')}>Failed</span>
              <span style={sx(CAPT + 'text-align:right;')}>Time</span>
            </div>
            {suites.map((s) => (
              <div key={s.suite + s.runner} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 90px 90px 90px;gap:12px;align-items:center;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
                  <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (s.failed || !s.available ? '#B3261E' : '#1F6B52') + ';')} />
                  <span style={sx('font-size:16px;color:#22272E;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(s.suite)}</span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#12503C;text-align:right;')}>{I(s.available ? String(s.passed) : '–')}</span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;text-align:right;color:' + (s.failed ? '#B3261E' : '#54606C') + ';')}>{I(s.available ? String(s.failed) : '–')}</span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#54606C;text-align:right;')}>{I(s.available ? secs(s.durationMs) : 'not run')}</span>
              </div>
            ))}
            <div style={sx('font-size:16px;color:#54606C;margin-top:11px;line-height:1.55;')}>
              {I(res.failed
                ? res.failed + ' failing case' + (res.failed === 1 ? ' blocks' : 's block') + ' the release. The safety suites are the gate: a red there is never overridden by a green elsewhere.'
                : unavailable.length
                  ? unavailable.map((s) => s.suite + ' could not run' + (s.error ? ': ' + s.error : '')).join('. ') + '. A suite that did not run is not a pass.'
                  : 'Every suite green. The safety suites carry the veto, so this is the only state a release ships from.')}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
